import classNames from 'classnames';
import React from 'react';

import Caret from '../../../components/icons/Caret';
import { Heading3 } from '../../../components/Text';
import { AccordionAnimation } from '../../../utils/animations/accordion';
import { sanitizeHtml } from '../../../utils/sanitzeHtml';
import { usePrefersReducedMotion } from '../../../utils/usePrefersReducedMotion';

import textStyle from '../../../components/Text/text-base.module.css';
import style from './accordion-item.module.css';

export type AccordionItemIconType = 'Chevron' | 'Plus';

interface AccordionItemProps {
  content: string;
  iconType: AccordionItemIconType;
  title: string;
  withAbbreviation: boolean;
  withDarkBackground: boolean;
  withLargeItemTitle: boolean;
}

const AccordionItem = ({
  content,
  iconType,
  title,
  withAbbreviation,
  withDarkBackground,
  withLargeItemTitle,
}: AccordionItemProps) => {
  const detailsId = title.replace(/[^a-zA-Z0-9]/g, '').replaceAll(' ', '-').toLowerCase();

  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    const detailsElement = document.querySelector<HTMLDetailsElement>(`#${detailsId}`);
    if (detailsElement) {
      const animation = new AccordionAnimation(detailsElement, { prefersReducedMotion });

      return () => {
        animation.removeEventListener();
      };
    }
  }, [detailsId, prefersReducedMotion]);

  return (
    <details
      className={classNames(
        style.details,
        withDarkBackground ? style.detailsDarkBackground : style.detailsLightBackground,
      )}
      id={detailsId}
    >
      <summary
        className={classNames(
          style.summary,
          withDarkBackground ? style.summaryDarkBackground : style.summaryLightBackground,
          AccordionAnimation.summaryClassName,
        )}
      >
        {withAbbreviation && (
          <abbr
            className={style.accordionItemAbbr}
            title={title}
          >
            {title.charAt(0)}
          </abbr>
        )}

        <Heading3
          {...withLargeItemTitle
            ? {
              fontSizeOverride: 24,
            }
            : {
              fontSize: 18,
            }}
        >
          {title}
        </Heading3>

        {iconType === 'Chevron'
          ? (
            <Caret />
          )
          : (
            <div
              className={classNames(
                style.plus,
                withDarkBackground ? style.plusAxisDarkBackground : style.plusIconLightBackground,
              )}
            >
              <div />
              <div className={style.plusY} />
            </div>
          )}
      </summary>

      <div
        className={classNames(
          textStyle.fontSize15,
          style.accordionItemDescription,
          AccordionAnimation.contentClassName,
        )}
        // eslint-disable-next-line @typescript-eslint/naming-convention
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
      />
    </details>
  );
};

export default AccordionItem;
